function initCartStore() {
  console.log('Alpine cart store init');
  const STORAGE_KEY = 'tp_products_free';

  Alpine.store('cart', {
    items: [],
    itemsCount: 0,

    // Dollars (Number) — unchanged shape so existing x-text bindings keep working
    totalPrice: 0,
    originalTotalPrice: 0,
    totalDiscount: 0,

    // Cents — use these for any new math
    totalPriceCents: 0,
    originalTotalPriceCents: 0,
    totalDiscountCents: 0,

    isCartOpen: false,
    isLoading: false,
    debounceUpdate: null,
    errorMessage: '',
    _footerPaddingObserver: null,

    // Set true if Discount Kit / Function discounts lag behind /cart/change.js
    // responses. Costs one extra GET /cart.js per mutation.
    revalidateAfterMutation: false,

    shippingDiscount: {
      shippingProgress: 0,
      shippingPrice: (window.ALP_CART_CONFIG && Number(window.ALP_CART_CONFIG.freeShippingThreshold)) || 35,
      shippingLeft: 35,
      isFreeShippingUnlocked: false,
    },

    // Generic quantity-gated free shipping progress, driven entirely by line
    // item properties — not tied to any one section. Any on-page offer can
    // stamp its items with `_free_shipping_group` (an id for that offer) and
    // `_free_shipping_required_qty` (how many units it takes to unlock).
    // This is a 0-100 PERCENTAGE (best group's qty / its required qty,
    // capped at 100), not just a boolean, so a progress bar can fill up
    // smoothly as units are added instead of jumping straight to 100% only
    // once the threshold is hit. Recomputed from scratch in
    // updateTotalPrice() on every cart sync, so removing items naturally
    // brings it back down too. Consumed by alp-6-progress-bar.liquid, which
    // takes the max of this and its own $-based progress.
    offerFreeShippingPercent: 0,

    // Ladder Offer (CNV-5434) — One-Time Purchase quantity discount. OTP
    // items carry no selling plan, so the discount is a real Shopify
    // discount code (Discount Code + URL-hack) applied at checkout, not
    // visible in cart.total_price beforehand. Recomputed from scratch in
    // updateTotalPrice() from any line's `_otp_ladder_code` property, so
    // it clears itself once every tagged line is gone. Empty string = no
    // ladder discount code currently in the cart.
    otpLadderCode: '',
    otpLadderPercent: 0,

    options: {
      showSellingPlan: true,
    },

    productsFree: [],

    // --- Load from LocalStorage ---
    loadProductsFree() {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          this.productsFree = parsed;
        }
      } catch (e) {
        console.warn('Failed to load productsFree from storage', e);
      }
    },

    // --- Save to LocalStorage ---
    saveProductsFree() {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.productsFree));
      } catch (e) {
        console.warn('Failed to save productsFree to storage', e);
      }
    },

    /**
     * Single place where server cart data becomes store state.
     * `cartData` should be a full cart object (/cart.js or /cart/change.js response).
     */
    syncFromCart(cartData) {
      if (cartData && Array.isArray(cartData.items)) {
        this.items = cartData.items;
      }
      this.updateItemsCount();
      this.updateTotalPrice(cartData);
    },

    async loadCart() {
      this.isLoading = true;
      this.errorMessage = '';

      try {
        const resp = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const data = await resp.json();
        this.syncFromCart(data);
      } catch (err) {
        this.errorMessage = 'Failed to load the cart. Please try again.';
      } finally {
        this.initRecommendationsCarousel();
        this.syncRecommendationsFooterPadding();
        this.isLoading = false;
      }

      this.loadProductsFree();
    },

    addItemsToFreeProducts(item) {
      this.productsFree = this.productsFree || [];
      const parentId = item?.[0]?.id;
      if (!parentId) {
        console.warn('No parent ID found in item', item);
        return;
      }
      const exists = this.productsFree.some(entry => entry?.[0]?.id === parentId);
      if (!exists) {
        this.productsFree.push(item);
        this.saveProductsFree();
      }
    },

    async addMultiplyToCart(productsArr) {
      this.isLoading = true;
      this.errorMessage = '';

      const formData = { items: productsArr };

      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        await this.loadCart();
        await this.refreshSections();

        window.location.href = '/checkout';
      } catch (err) {
        this.errorMessage = 'Failed to add the product. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    async updateCart(itemKey, quantity) {
      this.isLoading = true;
      this.errorMessage = '';

      try {
        const resp = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: String(itemKey), quantity })
        });

        if (!resp.ok) throw new Error('Failed to update the cart.');

        const data = await resp.json();
        this.syncFromCart(data);
        if (this.revalidateAfterMutation) await this.revalidate();
        await this.refreshSections();
      } catch (err) {
        this.errorMessage = err.message;
      } finally {
        this.isLoading = false;
      }
    },

    async clearCart() {
      this.isLoading = true;
      this.errorMessage = '';
      try {
        await fetch('/cart/clear.js', { method: 'POST' });
        this.syncFromCart({ items: [], total_price: 0, original_total_price: 0 });
        await this.refreshSections();
      } catch (err) {
        this.errorMessage = 'Failed to clear the cart. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    async removeFromCart(itemKey) {
      await this.updateCart(itemKey, 0);
    },

    async refreshCart() {
      await this.loadCart();
      await this.refreshSections();
      this.toggleCart();
    },

    /** Lightweight re-read of authoritative totals after a mutation. */
    async revalidate() {
      try {
        const resp = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const data = await resp.json();
        this.syncFromCart(data);
      } catch (e) {
        console.warn('Cart revalidate failed', e);
      }
    },

    toggleCart(status = null) {
      const wantsToOpen = status === true || (status === null && !this.isCartOpen);
      if (wantsToOpen && window.isAlpineCartExperience && !window.isAlpineCartExperience()) return;
      if (status === null) {
        this.isCartOpen = !this.isCartOpen;
      } else {
        this.isCartOpen = Boolean(status);
      }
      document.body.style.overflow = this.isCartOpen ? 'hidden' : '';
      document.querySelectorAll('[data-alp-cart-toggle]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', String(this.isCartOpen));
      });
    },

    updateItemsCount() {
      this.itemsCount = this.items.reduce((total, item) => total + item.quantity, 0);
      document.querySelectorAll('[data-alp-cart-toggle]').forEach((toggle) => {
        toggle.classList.toggle('cart__toggle--has-items', this.itemsCount > 0);
      });
    },

    showQuantity(itemId) {
      const item = this.items.find((item) => item.id === itemId);
      return item ? item.quantity : 0;
    },

    /**
     * Totals come from the server, never from summing item.price.
     * item.price excludes discount allocations (free gifts, Discount Kit,
     * automatic discounts) — only selling-plan adjustments are baked in.
     */
    updateTotalPrice(cartData = null) {
      let totalCents;
      let originalCents;

      if (cartData && typeof cartData.total_price === 'number') {
        totalCents = cartData.total_price;
        originalCents = typeof cartData.original_total_price === 'number'
          ? cartData.original_total_price
          : cartData.total_price;
      } else {
        // Fallback: final_line_price is post-discount per line.
        totalCents = this.items.reduce((sum, item) => {
          const line = typeof item.final_line_price === 'number'
            ? item.final_line_price
            : (typeof item.final_price === 'number' ? item.final_price : item.price) * item.quantity;
          return sum + line;
        }, 0);
        originalCents = this.items.reduce((sum, item) => {
          const line = typeof item.original_line_price === 'number'
            ? item.original_line_price
            : item.price * item.quantity;
          return sum + line;
        }, 0);
      }

      const discountCents = Math.max(0, originalCents - totalCents);

      this.totalPriceCents = totalCents;
      this.originalTotalPriceCents = originalCents;
      this.totalDiscountCents = discountCents;

      this.totalPrice = Number((totalCents / 100).toFixed(2));
      this.originalTotalPrice = Number((originalCents / 100).toFixed(2));
      this.totalDiscount = Number((discountCents / 100).toFixed(2));

      const threshold = this.shippingDiscount.shippingPrice;
      const total = this.totalPrice;

      this.shippingDiscount = {
        isFreeShippingUnlocked: total >= threshold,
        shippingLeft: +Math.max(0, threshold - total).toFixed(2),
        shippingProgress: Math.min(100, (total / threshold) * 100),
        shippingPrice: threshold
      };

      // See `offerFreeShippingPercent` above for what this is. Group by
      // (group id + required qty) rather than just group id, so two
      // instances of the same offer with different thresholds never bleed
      // into each other.
      const offerGroups = {};
      this.items.forEach((item) => {
        const props = (item && item.properties) || {};
        const group = props._free_shipping_group;
        const requiredQty = parseInt(props._free_shipping_required_qty, 10);
        if (!group || !requiredQty) return;
        const key = group + '::' + requiredQty;
        if (!offerGroups[key]) offerGroups[key] = { requiredQty, qty: 0 };
        offerGroups[key].qty += item.quantity;
      });
      this.offerFreeShippingPercent = Object.values(offerGroups).reduce(
        (best, g) => Math.max(best, Math.min(100, (g.qty / g.requiredQty) * 100)),
        0
      );

      // Ladder Offer (CNV-5434): the discount code (if any) tagged on the
      // current OTP stack. A build stamps every one of its lines with the
      // same code (see ctr-pouches-buy-box-v2.liquid's addToCart()), so the
      // first tagged line found is enough — cleared automatically once no
      // line carries the property any more (e.g. after cascade-removal).
      const ladderItem = this.items.find(
        (item) => item && item.properties && item.properties['_otp_ladder_code']
      );
      this.otpLadderCode = ladderItem ? ladderItem.properties['_otp_ladder_code'] : '';
      this.otpLadderPercent = ladderItem ? (parseInt(ladderItem.properties['_otp_ladder_percent'], 10) || 0) : 0;
    },

    /** Convenience getters for templates. */
    get formattedTotal() {
      return this.money(this.totalPriceCents);
    },
    get formattedOriginalTotal() {
      return this.money(this.originalTotalPriceCents);
    },
    get formattedDiscount() {
      return this.money(this.totalDiscountCents);
    },
    get hasDiscount() {
      return this.totalDiscountCents > 0;
    },

    money(cents) {
      const value = (Number(cents) || 0) / 100;
      try {
        return new Intl.NumberFormat(
          document.documentElement.lang || 'en-US',
          {
            style: 'currency',
            currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD'
          }
        ).format(value);
      } catch (e) {
        return '$' + value.toFixed(2);
      }
    },

    async addToCart({
      variantId,
      quantity = 1,
      properties = {},
      sellingPlanId = null,
      productId = null,
      maxPerOrder = null,
      maxPerOrderMessage = null,
    }, showCart = true) {
      if (!variantId) {
        console.error('Variant ID is required to add a product to the cart.');
        return Promise.reject('No variantId');
      }

      this.isLoading = true;
      this.errorMessage = '';

      const maxCap = maxPerOrder != null ? Number(maxPerOrder) : 0;
      if (maxCap > 0 && productId && window.maxPerOrder) {
        try {
          const cart = await window.maxPerOrder.fetchCart();
          const qtyAdd = Number(quantity) || 1;
          if (window.maxPerOrder.wouldExceedAdd(cart.items, productId, qtyAdd, maxCap)) {
            const msg =
              maxPerOrderMessage && String(maxPerOrderMessage).trim()
                ? String(maxPerOrderMessage).trim()
                : window.maxPerOrder.DEFAULT_MSG;
            window.maxPerOrder.showBlocked(msg);
            this.isLoading = false;
            return;
          }
        } catch (e) {
          console.error(e);
        }
      }

      const item = {
        id: Number(variantId),
        quantity: Number(quantity) || 1,
      };
      if (properties && Object.keys(properties).length > 0) {
        item.properties = properties;
      }
      if (sellingPlanId) {
        item.selling_plan = Number(sellingPlanId);
      }

      try {
        const resp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [item] }),
        });

        if (!resp.ok) {
          const errBody = await resp.json().catch(() => ({}));
          throw new Error(errBody.description || 'Failed to add the product. Please try again.');
        }

        await this.loadCart();
        await this.refreshSections();

        if (showCart) this.toggleCart();
      } catch (err) {
        this.errorMessage = (err && err.message) ? err.message : 'Failed to add the product. Please try again.';
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async refreshSections(sections = ['alp-1-items', 'alp-3-footer', 'alp-5-free-return', 'alp-2-recommendations']) {
      try {
        const resp = await fetch(`/cart?sections=${sections.join(',')}`);
        const data = await resp.json();

        sections.forEach(id => {
          const wrapper = document.querySelector(`#shopify-section-${id}`);
          if (data[id] && wrapper) {
            wrapper.outerHTML = data[id];
          }
        });
      } catch (err) {
        console.error('Failed to refresh cart sections', err);
      } finally {
        setTimeout(() => {
          this.initRecommendationsCarousel();
          this.syncRecommendationsFooterPadding();
        }, 50);
      }
    },

    updateCartDebounced({ line, quantity, key = null }) {
      clearTimeout(this.debounceUpdate);
      this.debounceUpdate = setTimeout(() => {
        this.changeQty({ line, quantity, key });
      }, 400);
    },

    initRecommendationsCarousel() {
      const swiperEl = document.querySelector('#swiper-cart-recommendations');
      if (!swiperEl || typeof Swiper === 'undefined') return;

      if (swiperEl.swiper) swiperEl.swiper.destroy(true, true);

      new Swiper(swiperEl, {
        slidesPerView: 1.2,
        spaceBetween: 12,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
      });
    },

    /**
     * #mini_cart--footer is `position: sticky; bottom: 0;` inside the cart
     * drawer, so it visually overlaps whatever content sits right above it
     * once the drawer's content is shorter than the sticky footer needs.
     * Give #mini_cart--recommendations a bottom padding equal to the
     * footer's current rendered height so its last row never ends up
     * hidden underneath the sticky footer.
     *
     * The footer's height isn't constant (discount banner, free-return
     * banner, subscription copy, locale text length, viewport width all
     * change it), and both the footer and the recommendations block get
     * replaced wholesale (outerHTML) on every refreshSections() call — so
     * we re-query fresh nodes and re-observe every time this runs instead
     * of caching references.
     */
    // CNV-7260: no longer pads the recommendations block by the footer
    // height. The footer is position: sticky inside the scrolling drawer, so
    // it already takes its own space; the extra padding-bottom only added an
    // empty gap (and clipped the taller "Customers also bought" cards).
    // Kept as a no-op cleanup because loadCart() / refreshSections() /
    // init still call it.
    syncRecommendationsFooterPadding() {
      if (this._footerPaddingObserver) {
        this._footerPaddingObserver.disconnect();
        this._footerPaddingObserver = null;
      }
      const recommendations = document.querySelector('#mini_cart--recommendations');
      if (recommendations) recommendations.style.paddingBottom = '';
    },

    async changeQty({ line, quantity, key = null }) {
      this.isLoading = true;

      if (quantity < 1) {
        await this.remove({ line, key });
        return;
      }

      const row = document.querySelector(`#alp-1-items .b-cart_item[data-line="${line}"]`);
      const max = row && parseInt(row.dataset.maxPerOrder, 10);
      const productId = row && row.dataset.productId;
      if (max > 0 && productId && window.maxPerOrder) {
        try {
          const cart = await window.maxPerOrder.fetchCart();
          if (window.maxPerOrder.wouldExceedLineChange(cart.items, productId, line, quantity, max)) {
            window.maxPerOrder.showLineNote(row, window.maxPerOrder.messageForElement(row));
            await this.loadCart();
            await this.refreshSections();
            this.isLoading = false;
            return;
          }
          window.maxPerOrder.clearLineNote(row);
        } catch (e) {
          console.error(e);
        }
      }

      try {
        const resp = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(key ? { id: key, quantity } : { line, quantity })
        });
        const data = await resp.json();
        this.syncFromCart(data);
        if (this.revalidateAfterMutation) await this.revalidate();
        await this.refreshSections();
      } catch (err) {
        console.error('changeQty error', err);
        this.errorMessage = 'Failed to update the cart. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    async changeSellingPlan({ line, qty, planId, identifier }) {
      this.isLoading = true;

      try {
        const index = line - 1;
        const currentItem = this.items[index];

        if (!currentItem) {
          console.warn('No cart item found at line', line);
          this.isLoading = false;
          return;
        }

        // Skio Dynamic Box: `_dynamicBoxId`/`_dynamicBoxIndex`/`_dynamicBoxMinQty`
        // group a line item into one box subscription, and Skio's automatic
        // "Build a Box" discount keys off that same grouping (see
        // ctr-pouches-buy-box.liquid's addToCart(), which only attaches these on
        // the Subscribe & Save path in the first place). Unsubscribing this one
        // line can drop the group's still-subscribed quantity below its own
        // `_dynamicBoxMinQty` — when that happens the whole box no longer
        // qualifies for box pricing, so every sibling line sharing the same
        // `_dynamicBoxId` must be unsubscribed and untagged too, not just the
        // line the customer clicked.
        const updates = [{ item: currentItem, planId }];

        const dynamicBoxId = currentItem.properties && currentItem.properties['_dynamicBoxId'];
        if (!planId && dynamicBoxId) {
          const minQty = Number(currentItem.properties['_dynamicBoxMinQty']) || 0;
          const siblings = this.dynamicBoxSiblings(dynamicBoxId, currentItem);
          const remainingQty = siblings.reduce(function (sum, it) { return sum + it.quantity; }, 0);

          if (remainingQty < minQty) {
            siblings.forEach(function (it) {
              updates.push({ item: it, planId: null });
            });
          }
        }

        await this.applySellingPlanUpdates(updates, identifier, planId);

        await this.loadCart();
        await this.refreshSections();
      } catch (e) {
        console.error('changeSellingPlan error', e);
        this.errorMessage = (e && e.message) ? e.message : 'Failed to update the subscription. Please try again.';
        document.dispatchEvent(new CustomEvent('cart:toast', {
          detail: { type: 'error', message: this.errorMessage }
        }));
        // Re-sync from the server no matter which fetch failed, so the UI
        // reflects the cart's real state instead of a stale pre-change view.
        await this.loadCart();
        await this.refreshSections();
      } finally {
        this.isLoading = false;
      }
    },

    /** Lines still on a Subscribe & Save plan sharing `dynamicBoxId`, excluding `exclude`. */
    dynamicBoxSiblings(dynamicBoxId, exclude = null) {
      return this.items.filter(function (it) {
        return it !== exclude
          && it.properties
          && it.properties['_dynamicBoxId'] === dynamicBoxId
          && it.selling_plan_allocation;
      });
    },

    /**
     * Every current line tagged with the same One-Time Purchase ladder
     * discount code (CNV-5434), excluding `exclude`. Unlike a Dynamic Box
     * (which downgrades on removal — see remove() below), a partial OTP
     * ladder stack no longer qualifies for its discount code at all, so
     * every sibling found this way gets deleted outright, not downgraded.
     */
    ladderOfferSiblings(ladderCode, exclude = null) {
      return this.items.filter(function (it) {
        return it !== exclude
          && it.properties
          && it.properties['_otp_ladder_code'] === ladderCode;
      });
    },

    /**
     * Removes then re-adds each `{item, planId}` pair in one batch — the only
     * way to change a cart line's selling plan via /cart/change.js + /cart/add.js.
     * Strips Dynamic Box tagging (and stamps `_hideSubscribeUpsell`) on any
     * line whose planId is null. Shared by changeSellingPlan (direct toggle)
     * and the Dynamic Box cascade in remove() (line removed outright).
     */
    async applySellingPlanUpdates(updates, identifier = null, planId = null) {
      // Remove every affected line highest-line-number first, so removing
      // one doesn't shift the line number of another not-yet-removed line.
      const removals = updates
        .map(function (u) { return { line: this.items.indexOf(u.item) + 1 }; }, this)
        .sort(function (a, b) { return b.line - a.line; });

      for (const removal of removals) {
        const removeResp = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: removal.line, quantity: 0 })
        });

        if (!removeResp.ok) {
          const errBody = await removeResp.json().catch(() => ({}));
          throw new Error(errBody.description || 'Failed to update the subscription. Please try again.');
        }
      }

      // Re-add every affected line in one batch, stripping Dynamic Box
      // tagging from any whose planId is null (the toggled line, plus any
      // cascaded siblings).
      const itemsPayload = updates.map(function (u) {
        const variantId = u.item.variant_id || u.item.id;
        let properties = u.item.properties || {};
        const wasDynamicBox = !!(properties && properties['_dynamicBoxId']);

        if (!u.planId) {
          const withoutDynamicBox = {};
          Object.keys(properties).forEach(function (key) {
            if (key.indexOf('_dynamicBox') !== 0) withoutDynamicBox[key] = properties[key];
          });
          properties = withoutDynamicBox;

          // Once a Dynamic Box line is unsubscribed, re-subscribing it alone
          // can never bring the "Build a Box Discount" back — the box has
          // already dissolved (that's what the de-tag above means). Showing
          // the SUBSCRIBE & SAVE toggle again would wrongly imply otherwise,
          // so hide it for good on this line via the theme-wide
          // `_hideSubscribeUpsell` convention (see the CNV-4859 comment at
          // the top of alp-1-items.liquid). The customer can still get box
          // pricing again — just by rebuilding a qualifying box from the
          // product page, not by re-toggling this one line.
          if (wasDynamicBox) {
            properties['_hideSubscribeUpsell'] = 'true';
          }
        }

        const newItem = { id: variantId, quantity: u.item.quantity };
        if (properties && Object.keys(properties).length > 0) {
          newItem.properties = properties;
        }
        if (u.planId) {
          newItem.selling_plan = Number(u.planId);
        }
        return newItem;
      });

      if (identifier && planId && Array.isArray(this.productsFree)) {
        const group = this.productsFree.find(g => g?.[0]?.id === identifier);
        if (group) {
          itemsPayload.push(...group.slice(1));
        }
      }

      const addResp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsPayload })
      });

      if (!addResp.ok) {
        // The old line(s) are already gone (removed above) and the replacement
        // failed to attach — the item(s) are now missing from the cart. This is
        // the worst outcome of this flow, so it gets its own explicit message
        // instead of the generic one below.
        const errBody = await addResp.json().catch(() => ({}));
        throw new Error(errBody.description || 'Your cart item could not be updated and may be missing. Please check your cart.');
      }
    },

    getLineByKey(lineItemKey) {
      const el = document.querySelector(`.b-cart_item[data-key="${lineItemKey}"]`);
      return el ? Number(el.dataset.line) : null;
    },

    async removeLinkedFreeGiftItems(parentLineKey, parentVariantId = null) {
      if (!parentLineKey && !parentVariantId) return;

      // TODO(cart-gifts): Several sections still add parent + gift through
      // slightly different flows. Prefer a single addParentWithLinkedGifts()
      // store method later, then remove the parentVariantId fallback below.
      const isLinkedGift = (item) => {
        const props = item && item.properties ? item.properties : {};
        return (parentLineKey && props._free_gift_parent_key === parentLineKey)
          || (parentVariantId && props._free_gift_parent_id === String(parentVariantId));
      };

      let guard = 0;
      let target = this.items.find(isLinkedGift);
      while (target && guard < 20) {
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: target.key, quantity: 0 })
          });
        } catch (e) {
          console.error('removeLinkedFreeGiftItems: failed to remove linked gift line', e);
          break;
        }
        await this.loadCart();
        target = this.items.find(isLinkedGift);
        guard++;
      }
    },

    async addFreeBonusItems(items, parent_id = null, lineItemKey = null) {
      if (!items || !items.length) return;

      this.isLoading = true;

      const bonusIds = items.map(i => Number(i.id));

      if (bonusIds.length) {
        // Re-read from this.items between removals — quantities/positions shift
        // after each change.
        //
        // IMPORTANT: target the line to remove by its `key` (via /cart/change.js's
        // `id` param), never by numeric `line` position. `line` position was
        // previously resolved by reading a `data-line` attribute off the
        // server-rendered #alp-1-items markup (see getLineByKey above) — but that
        // markup is only refreshed by refreshSections(), while this method is
        // called right after loadCart() (which updates this.items from a fresh
        // /cart.js read but does NOT touch the DOM). The moment a new line has
        // been added since the DOM was last rendered, every line position after
        // it is off by one, so a stale `data-line` can point at the wrong line —
        // in practice this was silently deleting the bundle item that had just
        // been added, instead of the old free-gift line. Removing by key is
        // immune to this because it's not positional.
        let guard = 0;
        let target = this.items.find(ci => bonusIds.includes(Number(ci.variant_id)));
        while (target && guard < 20) {
          try {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: target.key, quantity: 0 })
            });
          } catch (e) {
            console.error('addFreeBonusItems: failed to remove existing bonus line', e);
            break;
          }
          await this.loadCart();
          target = this.items.find(ci => bonusIds.includes(Number(ci.variant_id)));
          guard++;
        }
      }

      const payload = {
        items: items.map(i => {
          const properties = {
            ...(i.properties ? i.properties : {})
          };

          if (parent_id) properties._free_gift_parent_id = String(parent_id);
          if (lineItemKey) properties._free_gift_parent_key = String(lineItemKey);

          return {
            id: i.id,
            quantity: i.quantity || 1,
            ...(Object.keys(properties).length ? { properties } : {})
          };
        })
      };

      try {
        const resp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!resp.ok) {
          const errBody = await resp.json().catch(() => ({}));
          throw new Error(errBody.description || 'Failed to add the free gift. Please try again.');
        }

        // /cart/add.js does NOT return cart totals — must re-read.
        await this.loadCart();
        await this.refreshSections();
      } catch (e) {
        console.error('addFreeBonusItems error', e);
        throw e;
      } finally {
        this.isLoading = false;
      }
    },

    async remove({ line, key = null }) {
      this.isLoading = true;
      const wasOpen = this.isCartOpen;
      try {
        const removedItem = key
          ? this.items.find(item => item.key === key)
          : this.items[line - 1];
        const resp = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(key ? { id: key, quantity: 0 } : { line, quantity: 0 })
        });
        const data = await resp.json();
        this.syncFromCart(data);

        // Removing a line outright shrinks a Dynamic Box the same way
        // unsubscribing it would — if it drops the box's remaining
        // subscribed quantity below its `_dynamicBoxMinQty`, cascade the
        // same unsubscribe+untag used by changeSellingPlan to every sibling
        // line, so the SUBSCRIBE & SAVE toggle stops showing on them too.
        const dynamicBoxId = removedItem && removedItem.properties && removedItem.properties['_dynamicBoxId'];
        if (dynamicBoxId) {
          const minQty = Number(removedItem.properties['_dynamicBoxMinQty']) || 0;
          const siblings = this.dynamicBoxSiblings(dynamicBoxId);
          const remainingQty = siblings.reduce(function (sum, it) { return sum + it.quantity; }, 0);

          if (siblings.length && remainingQty < minQty) {
            const updates = siblings.map(function (it) { return { item: it, planId: null }; });
            await this.applySellingPlanUpdates(updates);
            await this.loadCart();
          }
        }

        // Ladder Offer (CNV-5434): a One-Time Purchase ladder stack only
        // qualifies for its discount code as a whole — there's no partial
        // "downgrade" state like a Dynamic Box has. So removing any one
        // tagged line deletes every sibling that shares its `_otp_ladder_code`
        // outright (highest line number first, so removing one doesn't shift
        // another not-yet-removed line's index).
        const ladderCode = removedItem && removedItem.properties && removedItem.properties['_otp_ladder_code'];
        if (ladderCode) {
          const ladderSiblings = this.ladderOfferSiblings(ladderCode);
          if (ladderSiblings.length) {
            const removals = ladderSiblings
              .map((it) => ({ line: this.items.indexOf(it) + 1 }))
              .sort((a, b) => b.line - a.line);
            for (const removal of removals) {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line: removal.line, quantity: 0 })
              });
            }
            const clearResp = await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ discount: '' })
            });
            const clearedCart = await clearResp.json();
            this.syncFromCart(clearedCart);
          }
        }

        await this.removeLinkedFreeGiftItems(
          removedItem && removedItem.key,
          removedItem && (removedItem.variant_id || removedItem.id)
        );
        if (this.revalidateAfterMutation) await this.revalidate();
        await this.refreshSections();
        // Removing a line item should never close the drawer on its own —
        // reassert the open state in case something else (e.g. a third-party
        // cart-sync script reacting to this /cart/change.js call) flipped it.
        if (wasOpen && !this.isCartOpen) this.toggleCart(true);
      } catch (e) {
        console.error('remove error', e);
      } finally {
        this.isLoading = false;
      }
    }
  });

  const cartStore = Alpine.store('cart');
  // Server-rendered markup (footer + recommendations) is already in the DOM
  // at this point — apply the padding immediately instead of waiting on the
  // first /cart.js round trip inside loadCart().
  cartStore.syncRecommendationsFooterPadding();
  cartStore.loadCart();

  if (!window.__alpCartThemeBridge) {
    window.__alpCartThemeBridge = true;

    document.querySelectorAll('[data-alp-cart-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', (event) => {
        if (window.isAlpineCartExperience && !window.isAlpineCartExperience()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        cartStore.toggleCart();
      });
    });

    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const action = form.getAttribute('action') || '';
      if (!action.includes('/cart/add')) return;
      if (window.isAlpineCartExperience && !window.isAlpineCartExperience()) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      cartStore.isLoading = true;
      cartStore.errorMessage = '';

      try {
        const response = await fetch(action, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          },
          body: new FormData(form)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Unable to add this item to the cart.');

        await cartStore.loadCart();
        await cartStore.refreshSections();
        cartStore.toggleCart(true);
        document.dispatchEvent(new CustomEvent('theme:cart:added', {
          bubbles: true,
          detail: { alpCartHandled: true, item: result }
        }));
      } catch (error) {
        cartStore.errorMessage = error.message;
      } finally {
        cartStore.isLoading = false;
      }
    }, true);

    const syncCart = async (openAfterSync = false) => {
      await cartStore.loadCart();
      await cartStore.refreshSections();
      if (openAfterSync) cartStore.toggleCart(true);
    };

    document.addEventListener('theme:cart:added', (event) => {
      if (window.isAlpineCartExperience && !window.isAlpineCartExperience()) return;
      if (!event.detail?.alpCartHandled) syncCart(true);
    });
    document.addEventListener('theme:cart:update', () => {
      if (!window.isAlpineCartExperience || window.isAlpineCartExperience()) syncCart(false);
    });
    document.addEventListener('theme:cart:open', () => {
      if (!window.isAlpineCartExperience || window.isAlpineCartExperience()) syncCart(true);
    });
    document.addEventListener('cart:refresh', () => {
      if (!window.isAlpineCartExperience || window.isAlpineCartExperience()) syncCart(false);
    });
    document.addEventListener('cart:item-added', () => {
      if (!window.isAlpineCartExperience || window.isAlpineCartExperience()) syncCart(true);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && cartStore.isCartOpen) cartStore.toggleCart(false);
    });
  }
}

if (window.Alpine) {
  initCartStore();
} else {
  document.addEventListener('alpine:init', initCartStore);
}
